import React, { useState, useEffect } from 'react';
import {
  FileText,
  Hash,
  Type,
  Calendar,
  Clock,
  Trash2,
  Search,
  Eye,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  MapPin,
  BookOpen,
  Users,
  File,
  Image,
  FileArchive,
  X,
  CheckCircle,
  AlertCircle,
  Copy,
  Link,
  Locate,
  MapPinIcon,
  XIcon
} from 'lucide-react';
import './OnlineSubmissions.css';
import { ApiUrl, BaseUrl } from '../API/ApiUrl';
import Swal from 'sweetalert2';

const OnlineSubmissions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchSubmissions = async () => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${ApiUrl}/online-submission`, {
        headers: {
          Authorization: `Bearer ${token}`, // if protected
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      setSubmissions(data);

    } catch (error) {
      console.error("Fetch Submissions Error:", error);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleDelete = async (id) => {

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This submission will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    const token = localStorage.getItem("adminToken");

    try {


      const response = await fetch(`${ApiUrl}/online-submission/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      // Refresh list
      fetchSubmissions();

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Submission has been deleted successfully.",
        timer: 2000,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error("Delete Submission Error:", error);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error.message || "Something went wrong!",
      });
    }
  };

  const handleView = async (item) => {
    const token = localStorage.getItem("adminToken");

    try {
      await fetch(`${ApiUrl}/online-submission/read/${item.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const response = await fetch(
        `${ApiUrl}/online-submission/${item.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }
      fetchSubmissions();

      setSelectedSubmission(data); // full data with authors
      setShowModal(true);

    } catch (error) {
      console.error("View Error:", error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'under-review': { label: 'Under Review', class: 'under-review' },
      'accepted': { label: 'Accepted', class: 'accepted' },
      'revision': { label: 'Revision', class: 'revision' },
      'rejected': { label: 'Rejected', class: 'rejected' }
    };
    const config = statusConfig[status] || { label: status, class: 'default' };
    return null;
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FileText size={14} className="file-icon pdf" />;
      case 'doc':
      case 'docx':
        return <FileText size={14} className="file-icon word" />;
      case 'jpg':
      case 'png':
      case 'gif':
        return <Image size={14} className="file-icon image" />;
      case 'zip':
      case 'rar':
        return <FileArchive size={14} className="file-icon archive" />;
      default:
        return <File size={14} className="file-icon" />;
    }
  };

  // Filter submissions based on search
  const filteredSubmissions = submissions.filter(item =>
    item.paper_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.corresponding_author_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.paper_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.keywords?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, startIndex + itemsPerPage);

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "", time: "" };

    const dateObj = new Date(dateString);

    const date = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const time = dateObj.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date, time };
  };

  const { date, time } = formatDateTime(selectedSubmission?.created_at || "");



  const handleDownloadFiles = (files) => {
    if (files) {
      window.open(`${BaseUrl}/uploads/${files}`, '_blank');
    }
  };

useEffect(() => {
  setCurrentPage(1);
}, [searchTerm ]);

useEffect(() => {
  if (currentPage > totalPages) {
    setCurrentPage(1);
  }
}, [totalPages]);

  return (
    <div className="online-submissions-page">
      {/* Header */}
      <div className="page-header">
        <div className="title-section">
          <FileText size={24} className="title-icon" />
          <div>
            <h1 className="page-title">Online Submissions</h1>
            <p className="page-subtitle">View and manage submitted manuscripts</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by title, author, type, keywords..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
                      <XIcon
                        size={16}
                        className="clear-icon"
                        onClick={() => setSearchTerm("")}
                      />
                    )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <h3>Submissions List</h3>
          <span className="record-count">{filteredSubmissions.length} submissions</span>
        </div>

        <div className="table-responsive">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Paper Title</th>
                <th>Corresponding Author</th>
                <th>Paper Type</th>
                {/* <th>Status</th> */}
                <th>Date / Time</th>
                <th>View</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubmissions.map((item, index) => (
                <tr key={item.id} className={item.is_read === 0 ? 'unread-row' : ''}>
                  <td>
                    <span className="sl-no">{startIndex + index + 1}</span>
                  </td>
                  <td>
                    <div className="title-wrapper">
                      <FileText size={14} className="name-icon" />
                      <span className="paper-title" title={item.paper_title}>
                        {item.paper_title.length > 50
                          ? `${item.paper_title.substring(0, 50)}...`
                          : item.paper_title}
                      </span>
                      {item.is_read === 0 && (
                        <span className="unread-badge">New</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="author-wrapper">
                      <User size={14} className="author-icon" />
                      <div>
                        <div className="author-name">{item.corresponding_author_name}</div>
                        <div className="author-email">{item.corresponding_author_email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="paper-type">{item.paper_type}</span>
                  </td>
                  {/* <td>
                    {getStatusBadge(item.status)}
                  </td> */}
                  <td>
                    {(() => {
                      const { date, time } = formatDateTime(item.created_at);

                      return (
                        <>
                          <div className="datetime-wrapper">
                            <Calendar size={12} className="datetime-icon" />
                            <span className="date">{date}</span>
                            <Clock size={12} className="datetime-icon time-icon" />
                            <span className="time">{time}</span>
                          </div>
                        </>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleView(item)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSubmissions.length === 0 && (
            <div className="empty-state">
              <FileText size={40} />
              <p>No submissions found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredSubmissions.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>


      {showModal && selectedSubmission && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="submission-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submission Details</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {/* Paper Title */}
              <div className="detail-section">
                <h4>
                  <FileText size={16} />
                  Paper Title
                </h4>
                <p className="paper-title-full">{selectedSubmission.paper_title}</p>
              </div>

              {/* Authors */}
              <div className="detail-section">
                <h4>
                  <Users size={16} />
                  Corresponding Authors
                </h4>
                <div className="authors-list">
                  <div className="author-item">
                    <div className="author-info">
                      <User size={14} />
                      <span className="author-name">{selectedSubmission.corresponding_author_name}</span>

                    </div>
                    <div className="author-email">
                      <Mail size={12} />
                      <span>{selectedSubmission.corresponding_author_email}</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedSubmission?.authors?.length > 0 && (
                <div className="detail-section">
                  <h4>
                    <Users size={16} />
                    Authors
                  </h4>

                  <div className="authors-list">
                    {selectedSubmission.authors.map((author, idx) => (
                      <div key={idx} className="author-item">
                        <div className="author-info">
                          <User size={14} />
                          <span className="author-name">{author.author_name}</span>

                          {author.isCorresponding && (
                            <span className="corresponding-badge">Corresponding</span>
                          )}
                        </div>

                        <div className="author-email">
                          <Mail size={12} />
                          <span>{author.author_email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Secondary Info & Address */}
              <div className="detail-row">
                <div className="detail-half">
                  <h4>
                    <Users size={14} />
                    Secondary Info
                  </h4>
                  <p>{selectedSubmission.secondary_info}</p>
                </div>
                <div className="detail-section">
                  <h4>
                    <MapPin size={14} />
                    Address
                  </h4>
                  <p>{selectedSubmission.address}</p>
                </div>
              </div>

              {/* Paper Type & Status */}
              <div className="detail-row">
                <div className="detail-half">
                  <h4>
                    <Type size={14} />
                    Paper Type
                  </h4>
                  <p>{selectedSubmission.paper_type}</p>
                </div>
                {/* <div className="detail-half">
                  <h4>
                    <AlertCircle size={14} />
                    Status
                  </h4>
                  <div className="status-wrapper">
                    {getStatusBadge(selectedSubmission.status)}
                  </div>
                </div> */}
                <div className="detail-section">
                  <h4>
                    <User size={16} />
                    Suggested Reviewers
                  </h4>
                  <p>{selectedSubmission.suggested_reviewer}</p>
                </div>
              </div>

              {/* Suggested Reviewer */}


              {/* Abstract */}
              <div className="detail-section">
                <h4>
                  <BookOpen size={16} />
                  Abstract
                </h4>
                <p className="abstract-text">{selectedSubmission.abstract}</p>
              </div>

              {/* Keywords */}
              <div className="detail-section">
                <h4>
                  <Hash size={16} />
                  Keywords
                </h4>
                <div className="keywords-container">
                  {selectedSubmission.keywords.split(',').map((keyword, idx) => (
                    <span key={idx} className="keyword-tag">{keyword.trim()}</span>
                  ))}
                </div>
              </div>

              {/* Files */}
              <div className="detail-section">
                <h4>
                  <File size={16} />
                  Files
                </h4>
                <div className="files-list">
                  {selectedSubmission.copyright_file && (
                    <div className="file-item">
                      {getFileIcon(selectedSubmission.copyright_file)}
                      <span className="file-name">{selectedSubmission.copyright_file}</span>
                      <span className="file-label">Copyright</span>
                      <button className="file-action" title="Download"
                        onClick={() => handleDownloadFiles(selectedSubmission.copyright_file)}

                      >
                        <Download size={14} />
                      </button>
                    </div>
                  )}
                  {selectedSubmission.paper_file && (
                    <div className="file-item">
                      {getFileIcon(selectedSubmission.paper_file)}
                      <span className="file-name">{selectedSubmission.paper_file}</span>
                      <span className="file-label">Manuscript</span>
                      <button className="file-action" title="Download"
                        onClick={() => handleDownloadFiles(selectedSubmission.paper_file)}

                      >
                        <Download size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-half">
                  <h4>
                    <Calendar size={16} />
                    Submitted On
                  </h4>
                  <p>{date} at {time}</p>
                </div>
                {selectedSubmission?.country && (
                  <div className="detail-section">
                    <h4>
                      <MapPinIcon size={16} />
                      Country
                    </h4>
                    <p>{selectedSubmission.country}</p>
                  </div>
                )}
              </div>
              {/* Created Date */}

            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
              {/* <button className="btn-primary">
                <Download size={14} />
                <span>Download All</span>
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineSubmissions;